// Global error context — collects structured backend errors and surfaces
// them through a single ErrorDialog. Two ways to trigger it:
//
//   1) Automatic, via the axios interceptor in utils/axiosSetup.js, which
//      dispatches a `chacal-api-error` CustomEvent whenever a response body
//      contains an `error_kind` field. The provider listens and shows the
//      dialog without any per-component wiring.
//
//   2) Manual, via const { showError } = useError(); showError(err);
//      Useful when a component wants to display an error from a non-axios
//      source, or to add a custom action handler.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import ErrorDialog from "../components/ErrorDialog";
import { normaliseApiError } from "../hooks/useStructuredError";

const ErrorContext = createContext({
  showError: () => {},
  clearError: () => {},
  error: null,
});

export function ErrorProvider({ children }) {
  const [error, setError] = useState(null);
  const [actionHandler, setActionHandler] = useState(null);

  const showError = useCallback((err, opts = {}) => {
    const envelope =
      err && typeof err === "object" && "isStructured" in err
        ? err
        : normaliseApiError(err);
    setError(envelope);
    if (opts.onAction) {
      setActionHandler(() => opts.onAction);
    } else {
      setActionHandler(null);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
    setActionHandler(null);
  }, []);

  // Listen for the global axios-interceptor event.
  useEffect(() => {
    const handler = (ev) => {
      // The interceptor passes the raw axios error so we get full context.
      showError(ev.detail);
    };
    window.addEventListener("chacal-api-error", handler);
    return () => window.removeEventListener("chacal-api-error", handler);
  }, [showError]);

  const handleAction = useCallback(
    (action, payload) => {
      if (actionHandler) {
        actionHandler(action, payload);
      }
      // The dialog always closes after an action; the caller can re-open
      // by calling showError again if needed.
      clearError();
    },
    [actionHandler, clearError],
  );

  return (
    <ErrorContext.Provider value={{ showError, clearError, error }}>
      {children}
      <ErrorDialog
        error={error}
        onClose={clearError}
        onAction={handleAction}
      />
    </ErrorContext.Provider>
  );
}

export function useError() {
  return useContext(ErrorContext);
}
